import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import ListDropdown from '../navigation/ListDropdown';
import { Link } from 'formlabs-web-components/Link';
import { Header } from 'formlabs-web-components/Type';
import { GridContainer, Grid, Cell } from 'formlabs-web-components/Grid';

import useIsMobile from '../../hooks/useIsMobile';
import useObserver from '../../hooks/useObserver';

import './TableOfContents.module.css';

const TableOfContents = ({
  children,
  levels,
  showHeadline = true,
  layout = 'sidebar'
}) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [toc, setToc] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const isMobile = useIsMobile(960);
  const [attach, detach] = useObserver(
    (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    },
    { rootMargin: '0% 0% -80% 0%' }
  );

  const headerLevels = (levels || [1, 2, 3, 4, 5, 6]).map(level => 'h' + level);
  const leastLevel = (levels && Math.min(...levels)) || 1;

  const displayHeadline =
    showHeadline &&
    (layout === 'sidebar' ? (
      <p styleName="sidebarHeader">{t('Table of Contents')}</p>
    ) : (
      <div styleName="headline">
        <Header level={4} displayAs={4}>
          {t('Quick Scroll to Section')}
        </Header>
      </div>
    ));

  // We have to act on rendered content because deep children structure does not allow for introspection
  useEffect(
    () => {
      if (!isMobile) {
        const content = ref.current;
        const allHeaders = content.querySelectorAll(headerLevels.join(','));

        const links = [...allHeaders].map(header => {
          const text = header.innerText;

          const id = header.id || encodeURIComponent(text);
          if (!header.id) {
            header.id = id;
          }

          attach(header);

          return {
            level: parseInt(header.tagName[1]) - leastLevel,
            id,
            text
          };
        });

        setToc(links);

        return () => {
          [...allHeaders].forEach(header => detach(header));
        };
      }
    },
    [children, isMobile]
  );

  if (isMobile && layout === 'sidebar') {
    return (
      <GridContainer>
        <Grid direction="x" primaryAlignment="align-left">
          <Cell smallSize={24}>{children}</Cell>
        </Grid>
      </GridContainer>
    );
  }
  if (isMobile && layout === 'fullWidth') {
    return (
      <GridContainer>
        <Grid direction="x" primaryAlignment="align-left">
          <Cell smallSize={24}>
            <div styleName="fullWidthMobile">
              {displayHeadline}
              <ListDropdown
                styling="tableOfContents"
                dropdownType="original"
                title={t('Select Material')}
              >
                {toc.map(entry => {
                  return {
                    name: entry.text,
                    url: `#${entry.id}`
                  };
                })}
              </ListDropdown>
            </div>
            {children}
          </Cell>
        </Grid>
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <Grid direction="x" primaryAlignment="align-left">
        <Cell smallSize={layout === 'sidebar' ? '6' : '24'}>
          {toc.length ? (
            <div styleName={layout}>
              {displayHeadline}
              <nav>
                <ul
                  style={{
                    gridTemplateRows:
                      (toc.length + 1) % 2 == 0
                        ? `repeat(${(toc.length + 1) / 2}, 1fr)`
                        : `repeat(${toc.length / 2}, 1fr)`
                  }}
                >
                  {toc.map((entry, i) => (
                    <Link key={entry.id} to={`#${entry.id}`}>
                      <li
                        styleName={classNames({
                          active: activeSection === entry.id
                        })}
                        style={{ paddingLeft: entry.level + 'rem' }}
                      >
                        {layout === 'fullWidth' && (
                          <span>
                            {i < 9 && '0'}
                            {i + 1}
                          </span>
                        )}
                        <p>{entry.text}</p>
                      </li>
                    </Link>
                  ))}
                </ul>
              </nav>
            </div>
          ) : null}
        </Cell>
        <Cell smallSize={layout === 'sidebar' ? '18' : '24'}>
          <div ref={ref}>{children}</div>
        </Cell>
      </Grid>
    </GridContainer>
  );
};

export default DisplayComponent(TableOfContents);
